// src/components/ChatBot.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../utilities/api';
import Spinner from './Spinner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane, faTimes } from '@fortawesome/free-solid-svg-icons';

function ChatBot({ isVisible, onClose, user }) {
  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const messagesEndRef = useRef(null); // Ref for scrolling

  // --- Scrolling Logic ---
  const scrollToBottom = () => {
    // Use timeout to ensure DOM has updated after state change
    setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 0);
  };

  // Scroll whenever messages change
  useEffect(() => {
    // Only scroll if the component is visible to avoid unnecessary actions
    if (isVisible) {
        scrollToBottom();
    }
  }, [messages, isVisible]); // Add isVisible dependency

  // --- Initial Fetch ---
  useEffect(() => {
    // Only fetch if visible, user exists, and no messages yet
    if (isVisible && user?.userid && messages.length === 0) {
      setIsLoading(true);
      setError('');
      api.chatBot({ messages: [] }) // Initial call
        .then(response => {
          if (response && response.success) {
            // Expecting a single object for the initial message
            if (response.messages && typeof response.messages === 'object' && !Array.isArray(response.messages)) {
              setMessages([response.messages]); // Wrap initial object in array
            } else {
              // Handle case where initial response format is not the expected object
              setError('Failed to initialize chat (invalid data).');
              setMessages([]);
            }
          } else {
            // Handle API error or unsuccessful response
            setError(response?.error || 'Failed to initialize chat.');
            setMessages([]);
          }
        })
        .catch(() => {
          setError('An error occurred while starting the chat.');
          setMessages([]);
        })
        .finally(() => {
          setIsLoading(false);
          // Scroll after initial load if successful
          if (messages.length > 0) { // Check if messages were actually set
             scrollToBottom();
          }
        });
    }
  }, [isVisible, user]); // Dependencies: only run when visibility or user changes

  // --- Send Message Logic ---
  const handleSend = useCallback(async () => {
    if (!userInput.trim() || isLoading || !user?.userid) return;

    const newUserMessage = { role: 'user', content: userInput.trim() };
    // Prepare message history to send (max 10, including new user message)
    const historyToSend = [...messages, newUserMessage].slice(-10);

    // Optimistically update UI
    setMessages(historyToSend);
    setUserInput('');
    setIsLoading(true);
    setError('');

    try {
      const response = await api.chatBot({ messages: historyToSend }); // Send updated history

      // --- Handle response (expecting single object) ---
      if (response && response.success && response.messages && typeof response.messages === 'object' && !Array.isArray(response.messages)) {
        // Append the new assistant message
        const assistantMessage = response.messages;
        // Use functional update to ensure we're working with the latest state
        setMessages(prevMessages => [...prevMessages, assistantMessage].slice(-10)); // Add new message and maintain limit
      } else if (response && response.error) {
        setError(response.error);
        // Keep optimistic user message, show error
      } else {
         // Handle unexpected response format (not success or missing message object)
         setError('Received an unexpected response from the chatbot.');
         // Keep optimistic user message
      }
    } catch {
      setError('An error occurred while sending your message.');
      // Keep optimistic user message
    } finally {
      setIsLoading(false);
      // Scroll after response handling (will be triggered by useEffect on messages change)
      // No need to call scrollToBottom() explicitly here if useEffect is set up correctly
    }
  }, [userInput, messages, isLoading, user]); // Dependencies for the callback

  // --- Key Press Handler ---
  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  return (
    // --- Main Container ---
    <div
      className={`fixed top-0 left-0 h-full w-full max-w-md bg-white shadow-xl z-40 transform transition-transform duration-300 ease-in-out flex flex-col
                 ${isVisible ? 'translate-x-0' : '-translate-x-full pointer-events-none'}`}
      aria-hidden={!isVisible}
      inert={!isVisible ? true : undefined}
    >
      {/* --- Header --- */}
      <div className="flex items-center justify-between p-3 border-b bg-primary text-white shrink-0">
        <h3 className="text-lg font-semibold">Chat Assistant</h3>
        <button onClick={onClose} className="text-white hover:text-gray-200 text-xl" aria-label="Close Chat">
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </div>

      {/* --- Messages Area (Reverted: No flex-col-reverse) --- */}
      <div className="flex flex-col justify-end flex-grow overflow-y-auto p-4 space-y-3 bg-highlight">

        {/* Initial Loading Spinner */}
        {isLoading && messages.length === 0 && (
            <div className="flex justify-center items-center h-full"><Spinner /></div>
        )}
        {/* Map Messages */}
        {Array.isArray(messages) && messages.map((msg, index) => (
          <div
            key={index} // Consider a more stable key if possible (e.g., timestamp or unique ID from API if available)
            className={`flex w-full ${msg.role === 'user' ? 'justify-start' : 'justify-end'}`}
          >
            <div
              className={`p-3 rounded-lg max-w-[66%] break-words text-sm shadow-md ${
                msg.role === 'user'
                  ? 'bg-white text-primary rounded-br-none'
                  : 'bg-secondary-light text-primary rounded-bl-none'
              }`}
            >
              {msg.content || ''}
            </div>
          </div>
        ))}
        {/* Ref div at the very end for scrolling */}
        <div ref={messagesEndRef} />
      </div>

      {/* --- Error Display --- */}
      {error && (
        <div className="p-2 bg-red-100 text-red-700 text-xs border-t border-b border-red-200 shrink-0">
          Error: {error}
        </div>
      )}

      {/* --- Input Area --- */}
      <div className="p-3 border-t bg-white shrink-0">
        <div className="flex items-center space-x-2">
          <input
            type="text"
            value={userInput}
            onChange={(e) => setUserInput(e.target.value)}
            onKeyUp={handleKeyPress}
            placeholder="Type your message..."
            className="flex-grow p-2 border border-neutral rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-sm"
            disabled={isLoading}
          />
          <button
            onClick={handleSend}
            className={`bg-button text-white p-2 rounded-md hover:bg-button-light disabled:opacity-50 disabled:cursor-not-allowed ${isLoading ? 'animate-pulse' : ''}`}
            disabled={isLoading || !userInput.trim()}
            aria-label="Send Message"
          >
             {isLoading ? <Spinner size="sm" color="text-white"/> : <FontAwesomeIcon icon={faPaperPlane} />}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ChatBot;
